import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Divider,
    Skeleton,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse,
    Tooltip,
    Card,
    CardMedia
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MovieIcon from '@mui/icons-material/Movie';
import { getPublishedEvent } from '@virtual-cinema/client-lib';

const ContentPlaylist = ({ eventId, currentContentId, onSelectContent }) => {
    const [eventData, setEventData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const [orderedMovies, setOrderedMovies] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    useEffect(() => {
        const fetchEventData = async () => {
            if (!eventId) return;

            setIsLoading(true);
            try {
                const { data, error } = await getPublishedEvent(eventId);
                if (error) throw new Error(error);
                setEventData(data);

                // Process the content to respect ordering
                if (data.content && data.content.length > 0) {
                    // Parse event details if it's a string
                    let eventDetails = data.event?.details;
                    if (typeof eventDetails === 'string' && eventDetails) {
                        try {
                            eventDetails = JSON.parse(eventDetails);
                        } catch (e) {
                            console.error("Error parsing event details:", e);
                            eventDetails = {};
                        }
                    }

                    // If contentOrder exists in event details, use it to order the content
                    if (
                        eventDetails &&
                        eventDetails.contentOrder &&
                        Array.isArray(eventDetails.contentOrder)
                    ) {
                        // Order content based on contentOrder
                        const ordered = orderContentByIds(data.content, eventDetails.contentOrder);
                        setOrderedMovies(ordered);
                    } else {
                        // Otherwise use content as is
                        setOrderedMovies(data.content);
                    }
                } else {
                    setOrderedMovies([]);
                }
            } catch (err) {
                console.error("Error fetching event data for playlist:", err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEventData();
    }, [eventId]);

    // Function to order content based on the contentOrder array
    const orderContentByIds = (contentList, orderIds) => {
        // Create a map of id to content for faster lookup
        const contentMap = contentList.reduce((map, item) => {
            map[item.id] = item;
            return map;
        }, {});

        // Create ordered array based on orderIds
        const ordered = [];

        // First add content in the specified order
        orderIds.forEach(id => {
            if (contentMap[id]) {
                ordered.push(contentMap[id]);
                delete contentMap[id]; // Remove from map to avoid duplicates
            }
        });

        // Add any remaining content that wasn't in orderIds
        Object.values(contentMap).forEach(item => {
            ordered.push(item);
        });

        return ordered;
    };

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    // Auto-collapse on mobile
    useEffect(() => {
        if (isMobile) {
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    }, [isMobile]);

    const handleContentClick = (content) => {
        if (onSelectContent) {
            onSelectContent(content);
        } else {
            navigate(`/event/${eventId}/content/${content.id}`);
        }
    };

    if (error) return null; // Don't show anything if there's an error

    return (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                // Use a fixed max-height rather than stretching to fill the space
                height: 'auto',
                maxHeight: { xs: 'auto', md: '600px' }, // Limit height on desktop
                mb: 3
            }}
        >
            {/* Header - now clickable */}
            <Box
                onClick={toggleExpanded}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.03)'
                    },
                    transition: 'background-color 0.2s'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MovieIcon sx={{ mr: 1, color: theme.palette.text.primary }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                        Event Playlist
                    </Typography>
                </Box>

                <Tooltip title={expanded ? "Collapse" : "Expand"}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation(); // Prevents the Box click handler from also firing
                            toggleExpanded();
                        }}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.08)'
                            }
                        }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Playlist Content */}
            <Collapse in={expanded} sx={{ overflowY: 'auto', maxHeight: { xs: '400px', md: '550px' } }}>
                {isLoading ? (
                    // Loading skeletons
                    <List sx={{ width: '100%', p: 1 }}>
                        {[...Array(3)].map((_, index) => (
                            <ListItem key={index} sx={{ p: 1 }}>
                                <ListItemAvatar sx={{ minWidth: 120 }}>
                                    <Skeleton variant="rectangular" width={110} height={62} />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={<Skeleton width="80%" height={24} />}
                                    secondary={<Skeleton width="40%" />}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <List sx={{ width: '100%', p: 0 }}>
                        {orderedMovies.map((movie, index) => {
                            const isCurrentContent = movie.id === currentContentId;
                            const info = movie.info || {};
                            const title = info.title || movie.title || movie.filename || "Untitled";

                            return (
                                <React.Fragment key={movie.id}>
                                    {index > 0 && <Divider sx={{ my: 0.5 }} />}
                                    <ListItem
                                        alignItems="flex-start"
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            backgroundColor: isCurrentContent ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                cursor: 'pointer'
                                            },
                                            transition: 'background-color 0.2s'
                                        }}
                                        onClick={() => handleContentClick(movie)}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%' }}>
                                            {/* Thumbnail */}
                                            <Box
                                                position="relative"
                                                sx={{
                                                    minWidth: { xs: 100, sm: 120, md: 140 },
                                                    marginRight: 2,
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    border: isCurrentContent ? '2px solid' : '1px solid',
                                                    borderColor: isCurrentContent ? 'primary.main' : 'divider'
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    image={movie.latest_asset?.asset_url || '/placeholder-image.jpg'}
                                                    alt={title}
                                                    sx={{
                                                        width: '100%',
                                                        height: 'auto',
                                                        aspectRatio: '16/9',
                                                        backgroundColor: '#000'
                                                    }}
                                                />
                                                {isCurrentContent && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: 'rgba(0,0,0,0.4)'
                                                        }}
                                                    >
                                                        <PlayArrowIcon
                                                            sx={{
                                                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                                                color: 'white'
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Content */}
                                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <Typography
                                                    variant="body1"
                                                    color="text.primary"
                                                    sx={{
                                                        fontWeight: isCurrentContent ? 'bold' : 'medium',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        lineHeight: 1.3,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {title}
                                                </Typography>

                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {info.duration && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ display: 'flex', alignItems: 'center' }}
                                                        >
                                                            <MovieIcon sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.7 }} />
                                                            {Math.floor(info.duration / 60)}:{(info.duration % 60).toString().padStart(2, '0')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Collapse>
        </Card>
    );
};

export default ContentPlaylist;